import { useContext, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { ConfirmationModal } from "./ConfirmationModal";
import { ExercisePauseButtonSlotContext } from "../exercises/ExercisePauseBoundary";

const actionCopy = {
  back: {
    title: "Zurück zu den Einstellungen?",
    message: "Der aktuelle Durchlauf wird beendet. Möchtest du wirklich zurück zu den Einstellungen?",
  },
  remix: {
    title: "Neu mischen?",
    message: "Der aktuelle Durchlauf wird beendet und mit einer neuen Mischung gestartet. Möchtest du fortfahren?",
  },
} as const;

export function exerciseMenuAction(buttonText: string) {
  if (buttonText.includes("Zurück zu den Einstellungen")) return "back" as const;
  if (buttonText.includes("Neu mischen")) return "remix" as const;
  return null;
}

export function ExerciseMenu({ children, label = "Aufgabenmenü" }: { children: ReactNode; label?: string }) {
  const setPauseButtonSlot = useContext(ExercisePauseButtonSlotContext);
  const [pending, setPending] = useState<{ action: keyof typeof actionCopy; button: HTMLButtonElement } | null>(null);
  const allowedButton = useRef<HTMLButtonElement | null>(null);

  function interceptAction(event: MouseEvent<HTMLElement>) {
    const button = (event.target as HTMLElement).closest("button");
    if (!button) return;
    if (allowedButton.current === button) {
      allowedButton.current = null;
      return;
    }
    const action = exerciseMenuAction(button.textContent ?? "");
    if (!action) return;
    event.preventDefault();
    event.stopPropagation();
    setPending({ action, button });
  }

  return (
    <nav className="exercise-menu" aria-label={label} onClickCapture={interceptAction}>
      <span className="exercise-pause-button-slot" ref={setPauseButtonSlot} />
      {children}
      {pending && (
        <ConfirmationModal
          title={actionCopy[pending.action].title}
          message={actionCopy[pending.action].message}
          onCancel={() => setPending(null)}
          onConfirm={() => {
            const button = pending.button;
            setPending(null);
            allowedButton.current = button;
            button.click();
          }}
        />
      )}
    </nav>
  );
}
